import {
	Dialog,
	FontIconName,
	Intersection,
	NoResults,
	TabsNavigation,
	TabsNavigationItemData,
	Toolbar
} from "client/Components";
import {
	AlertManager,
	createRemoteDataSource,
	LazyLoadCursorKind,
	NumberFormatter,
	RemoteDataSourceState,
	useAlerts,
	useForceRerender,
	useUnmountedCancellation
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { List } from "./List";
import styles from "./AttendeesDialog.module.less";
import {
	AttendeeCounter,
	AttendeeExportModel,
	AttendeePreviewModel,
	EventAttendeeKind,
	getAttendees,
	listAttendeesForExport
} from "client/Api/Hub/Events/eventViewer";
import { useHubStatisticsFormatter } from "../../useHubStatisticsFormatter";
import {
	downloadAsFile,
	formatDataAsCsv,
	generateTrackingId
} from "client/Misc/utils";
import { LocationType } from "client/Api/EventsManagement/eventsManagement";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getIdentifiesAs } from "client/Features/Settings/UserProfile/UserProfileForm";
const PAGE_SIZE = 50;

export type GetAttendeesCallback = (
	id: number,
	pageSize: number,
	skip: number,
	signal?: AbortSignal
) => Promise<LazyLoadPageResult<AttendeePreviewModel>>;

type AttendeesDialogProps = {
	eventId: number;
	eventName: string;
	attendeeCounts: readonly AttendeeCounter[];
	locationType: LocationType;
	isExportAvailable: boolean;
	onClose(): void;
};

export const AttendeesDialog = (props: AttendeesDialogProps): ReactElement => {
	const {
		eventId,
		eventName,
		attendeeCounts,
		locationType,
		isExportAvailable,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [activeTabId, setActiveTabId] = useState<EventAttendeeKind | undefined>(attendeeCounts.length === 1
		? attendeeCounts[0].kind
		: undefined
	);

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [forceReload, itemsVersion] = useForceRerender();
	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const isHybrid: boolean = locationType === LocationType.Hybrid;
	const totalAttendees: number = attendeeCounts
		.reduce((sum, a) => sum + a.count, 0);

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		hasLazyLoadError,
		retryToLoadLastPage
	} = useAttendeesDataSource({
		filtering: {
			eventId,
			kind: activeTabId
		},
		desiredAmountOfItems,
		globalContext: {
			itemsVersion
		},
	});

	const loadingState: RemoteDataSourceState = getLoadingState();

	const tabsData = useMemo(
		(): TabsNavigationItemData<EventAttendeeKind | undefined>[] => isHybrid
			? getAttendeeKindTabs(attendeeCounts, statisticsFormatter)
			: [],
		[
			isHybrid,
			attendeeCounts,
			statisticsFormatter
		]
	);

	const exportHandler = useCallback(
		async (): Promise<void> => {
			setIsBusy(true);

			try {
				const attendees: readonly AttendeeExportModel[] = await listAttendeesForExport(
					eventId,
					globalCancellation
				);

				downloadAttendeesAsCsv(eventName, attendees);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			eventId,
			eventName,
			globalCancellation
		]
	);

	const increaseAmount = useCallback(
		(): void => {
			const isLoading = loadingState === "loading";
			const areAllPreviouslyRequestedItemsLoaded: boolean = currentItems.length >= desiredAmountOfItems;

			if (!isLoading
				&& areAllPreviouslyRequestedItemsLoaded
				&& hasMoreOnServer
				&& !hasLazyLoadError
			) {
				setDesiredAmountOfItems(prevState => prevState + PAGE_SIZE);
			}
		},
		[
			currentItems.length,
			desiredAmountOfItems,
			hasLazyLoadError,
			hasMoreOnServer,
			loadingState
		]
	);

	const renderItems = (): ReactElement | null => {

		if (currentItems.length > 0) {
			return (
				<>
					<List
						items={currentItems}
					/>
					<Intersection onEnter={increaseAmount} />
					{hasLazyLoadError && (
						<NoResults
							trackingId="has-lazy-loading-error"
							icon="gear"
							title={localize("_NotAbleToRetrieveAttendees")}
							message={localize("_WeWereUnableToRetrieveAttendees")}
							onRetry={retryToLoadLastPage}
							hasExtraTopPadding
						/>
					)}
				</>
			);
		}

		switch (loadingState) {
			case "loading":
				return null;
			case "error":
				return (
					<NoResults
						trackingId="has-error"
						icon="gear"
						title={localize("_NotAbleToRetrieveAttendees")}
						message={localize("_WeWereUnableToRetrieveAttendees")}
						onRetry={forceReload}
						height="fill"
					/>
				);
			default:
		}

		return (
			<NoResults
				trackingId="has-no-results"
				icon="gear"
				title={localize("_NotAbleToRetrieveAttendees")}
				message={localize("_WeWereUnableToRetrieveAttendees")}
				height="fill"
			/>
		);
	};

	return (
		<Dialog
			hasBusyIndicator={isBusy || loadingState === "loading"}
			height="fill"
			trackingId="event-attendees-dialog"
		>
			<Dialog.Header
				title={`${localize("_Attendees")} (${statisticsFormatter(totalAttendees)})`}
				onClose={onClose}
			/>
			<Dialog.Body>
				<div
					className={styles.content}
				>
					{isHybrid && (
						<div className={styles.tabs}>
							<TabsNavigation
								value={activeTabId}
								items={tabsData}
								onChange={setActiveTabId}
								trackingId="attendee-kind-tabs"
							/>
						</div>
					)}
					<div className={styles.list}>
						{renderItems()}
					</div>
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				{isExportAvailable && (
					<Toolbar>
						<Toolbar.Button
							trackingId="export"
							text={localize("_ExportAllToCsv")}
							kind="secondary"
							icon="file-export"
							onClick={exportHandler}
						/>
					</Toolbar>
				)}
			</Dialog.Footer>
		</Dialog>
	);
};

AttendeesDialog.displayName = "Events.AttendeesDialog";

type AttendeeTabInfo = {
	text: LocalizationKey;
	icon: FontIconName;
};

export const attendeeKindToTabInfoMap: { [k in EventAttendeeKind]: AttendeeTabInfo; } = {
	[EventAttendeeKind.Online]: {
		text: "_Online",
		icon: "video"
	},
	[EventAttendeeKind.Physical]: {
		text: "_Physically",
		icon: "location-dot"
	}
};

export function getAttendeeKindTabs(
	attendeeCounts: readonly AttendeeCounter[],
	statisticsFormatter: NumberFormatter
): TabsNavigationItemData<EventAttendeeKind | undefined>[] {
	const tabs: TabsNavigationItemData<EventAttendeeKind | undefined>[] = [];
	const countersWithAttendees: readonly AttendeeCounter[] = attendeeCounts
		.filter(x => x.count > 0);

	const totalAttendees: number = attendeeCounts
		.reduce((sum, a) => sum + a.count, 0);

	if (countersWithAttendees.length > 1) {
		tabs.push({
			value: undefined,
			text: `${localize("_All")} (${totalAttendees})`,
			trackingId: generateTrackingId("attendee-tab", `|all`)
		});
	}

	for (const attendeeCount of attendeeCounts) {
		const {
			kind,
			count
		} = attendeeCount;

		if (!count) {
			continue;
		}

		const {
			text,
			icon
		} = attendeeKindToTabInfoMap[kind];

		tabs.push({
			value: kind,
			text: `${localize(text)} (${statisticsFormatter(count)})`,
			icon,
			trackingId: generateTrackingId("attendee-tab", `|kind-${kind}`)
		});
	}

	return tabs;
}

export function downloadAttendeesAsCsv(
	eventName: string,
	attendees: readonly AttendeeExportModel[]
): void {
	const fileContent: string = generateCsvContentForAttendees(attendees);

	let filename: string = eventName;

	if (filename.length > 50) {
		filename = filename.substring(0, 49) + "…";
	}

	downloadAsFile(
		fileContent,
		"text/csv",
		`${filename}.csv`
	);
}

function generateCsvContentForAttendees(users: readonly AttendeeExportModel[]): string {
	const headers: readonly string[] = [
		localize("_Pronouns"),
		localize("_FirstName"),
		localize("_LastName"),
		localize("_Email"),
		localize("_PhoneNumber"),
		localize("_JobTitle"),
		localize("_OfficeLocation"),
		localize("_Attendance")
	];

	const data: readonly (readonly string[])[] = users
		.map((x): readonly string[] => [
			getIdentifiesAs(x.pronouns) ?? "",
			x.firstName ?? "",
			x.lastName ?? "",
			x.primaryEmail ?? "",
			x.phoneNumber ?? "",
			x.jobTitle ?? "",
			x.officeLocation ?? "",
			localize(
				attendeeKindToTabInfoMap[x.attendeeKind]
					.text
			)
		]);

	return formatDataAsCsv(
		headers,
		data
	);
}

type FilterState = {
	eventId: number;
	kind: EventAttendeeKind | undefined;
};

type GlobalContext = {
	itemsVersion: number;
};

const useAttendeesDataSource = createRemoteDataSource<
	AttendeePreviewModel,
	FilterState,
	GlobalContext
>()
	.setConfiguration({
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake,
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<AttendeePreviewModel>> => {
				const {
					amount,
					skip,
					signal,
					filterState
				} = options;

				return await getAttendees(
					filterState.eventId,
					filterState.kind,
					amount,
					skip,
					signal
				);
			}
		}
	);
